import React from "react";
import {
  FaUserGraduate,
  FaIdCard,
  FaCalendarAlt,
  FaUsers,
  FaPhone,
  FaEnvelope,
  FaBus,
  FaBed,
  FaTint,
  FaLayerGroup,
  FaUser,
  FaClock,
  FaFilePdf,
  FaWalking,
  FaSun,
  FaUserShield,
} from "react-icons/fa";

export default function StudentDetails({
  student: incomingStudent,
  studentDetails,
  onClose,
}) {
  const student = incomingStudent || studentDetails;
  if (!student) return null;

  const DetailItem = ({
    icon: Icon,
    label,
    value,
    highlight = false,
    isLink = false,
  }) => (
    <div
      className={`flex items-start p-3 rounded-lg ${highlight ? "bg-blue-50 border border-blue-100" : "bg-gray-50"}`}
    >
      <div
        className={`mt-1 mr-3 ${highlight ? "text-blue-600" : "text-gray-400"}`}
      >
        <Icon />
      </div>
      <div className="flex-1 w-full overflow-hidden">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </p>
        <div
          className={`font-semibold ${highlight ? "text-blue-900" : "text-gray-800"} break-words`}
        >
          {isLink && value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              <FaFilePdf className="text-red-500" /> View Document
            </a>
          ) : (
            value || <span className="text-gray-400 italic">Not Provided</span>
          )}
        </div>
      </div>
    </div>
  );

  const PhotoBox = ({ src, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200 mb-2">
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-3xl">
            <FaUser />
          </div>
        )}
      </div>
      <div className="text-xs font-bold text-gray-600 uppercase bg-gray-100 px-2 py-1 rounded-full">
        {label}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl mx-auto overflow-hidden border border-gray-100 max-h-[90vh] flex flex-col">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-800 p-6 text-white shrink-0">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-6">
            <PhotoBox src={student.studentPhoto} label="Student" />
            <div>
              <h2 className="text-3xl font-bold !text-white drop-shadow-sm mb-1">
                {student.studentName}
              </h2>
              <div className="flex flex-wrap items-center gap-3 !text-blue-50 text-sm">
                <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                  <FaIdCard />
                  <span className="font-mono font-bold">
                    {student.admissionNumber}
                  </span>
                </div>
                <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                  <FaLayerGroup />
                  <span>
                    Class {student.class} - {student.section}
                  </span>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${student.isActive ? "bg-green-500/20 text-green-100 border-green-400" : "bg-red-500/20 text-red-100 border-red-400"}`}
                >
                  {student.isActive ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors text-white"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="p-6 space-y-8 overflow-y-auto custom-scrollbar flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Personal */}
          <div className="lg:col-span-2 space-y-8">
            {/* Academic Profile */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <FaUserGraduate className="mr-2 text-blue-600" /> Personal &
                Academic
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem
                  icon={FaCalendarAlt}
                  label="Date of Birth"
                  value={
                    student.dateOfBirth
                      ? new Date(student.dateOfBirth).toLocaleDateString()
                      : ""
                  }
                />
                <DetailItem
                  icon={FaTint}
                  label="Category"
                  value={student.category}
                />
                <DetailItem
                  icon={FaIdCard}
                  label="Aadhaar No"
                  value={student.studentAadhaarNo}
                />
                <DetailItem
                  icon={FaIdCard}
                  label="PAN No"
                  value={student.studentPANNo || student.studentPanNo || student.StudentPANNo || student.StudentPanNo}
                />
                <DetailItem
                  icon={FaIdCard}
                  label="Aadhaar File"
                  value={student.studentAadhaarFile}
                  isLink
                />
                <DetailItem
                  icon={FaCalendarAlt}
                  label="Admission Date"
                  value={new Date(student.dateOfAdmission).toLocaleDateString()}
                />
                <DetailItem
                  icon={FaCalendarAlt}
                  label="Session"
                  value={student.session}
                />
              </div>
            </section>

            {/* Family Details */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <FaUsers className="mr-2 text-blue-600" /> Family Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                {/* Father */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-2">
                    <PhotoBox src={student.fatherPhoto} label="Father" />
                    <div>
                      <div className="font-bold text-gray-800">
                        {student.fatherName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {student.fatherMobileNo}
                      </div>
                    </div>
                  </div>
                  <DetailItem
                    icon={FaIdCard}
                    label="Aadhaar No"
                    value={student.fatherAadhaarNo}
                  />
                  <DetailItem
                    icon={FaIdCard}
                    label="Aadhaar Doc"
                    value={student.fatherAadhaarFile}
                    isLink
                  />
                </div>

                {/* Mother */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-2">
                    <PhotoBox src={student.motherPhoto} label="Mother" />
                    <div>
                      <div className="font-bold text-gray-800">
                        {student.motherName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {student.motherMobileNo}
                      </div>
                    </div>
                  </div>
                  <DetailItem
                    icon={FaIdCard}
                    label="Aadhaar No"
                    value={student.motherAadhaarNo}
                  />
                  <DetailItem
                    icon={FaIdCard}
                    label="Aadhaar Doc"
                    value={student.motherAadhaarFile}
                    isLink
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Secondary Info */}
          <div className="space-y-8">
            {/* Guardians & Contact */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <FaPhone className="mr-2 text-blue-600" /> Contact & Guardian
              </h3>
              <div className="space-y-3">
                <DetailItem
                  icon={FaUserShield}
                  label="Local Guardian"
                  value={student.localGuardianName}
                />
                <DetailItem
                  icon={FaPhone}
                  label="Guardian Mobile"
                  value={student.localGuardianMobileNo}
                />
                <div className="border-t my-2"></div>
                <DetailItem
                  icon={FaPhone}
                  label="Primary Contact"
                  value={student.parentContactNo}
                  highlight
                />
                <DetailItem
                  icon={FaEnvelope}
                  label="Email"
                  value={student.parentEmail}
                />
              </div>
            </section>

            {/* Facilities & Logistics */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <FaBus className="mr-2 text-blue-600" /> Facilities & Logistics
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div
                  className={`p-3 rounded-xl flex items-center gap-3 border ${
                    student.transportRequired
                      ? "bg-blue-50 border-blue-200 text-blue-900"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                  }`}
                >
                  <FaBus
                    className={`text-xl ${student.transportRequired ? "text-blue-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Transport
                    </div>
                    <div className="text-xs font-bold">
                      {student.transportRequired ? "Required" : "Not Required"}
                    </div>
                  </div>
                </div>

                <div
                  className={`p-3 rounded-xl flex items-center gap-3 border ${
                    student.isDayBoarding
                      ? "bg-orange-50 border-orange-200 text-orange-900"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                  }`}
                >
                  <FaSun
                    className={`text-xl ${student.isDayBoarding ? "text-orange-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Day Boarding
                    </div>
                    <div className="text-xs font-bold">
                      {student.isDayBoarding ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                <div
                  className={`p-3 rounded-xl flex items-center gap-3 border ${
                    student.isPedestrian
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                  }`}
                >
                  <FaWalking
                    className={`text-xl ${student.isPedestrian ? "text-emerald-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Walker
                    </div>
                    <div className="text-xs font-bold">
                      {student.isPedestrian ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                <div
                  className={`p-3 rounded-xl flex items-center gap-3 border ${
                    student.hostelRequired
                      ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                  }`}
                >
                  <FaBed
                    className={`text-xl ${student.hostelRequired ? "text-indigo-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Hostel
                    </div>
                    <div className="text-xs font-bold">
                      {student.hostelRequired ? "Required" : "Not Required"}
                    </div>
                  </div>
                </div>
              </div>

              {student.transportWithGuardian && (
                <div className="mt-3 text-xs bg-purple-50 text-purple-700 p-2.5 rounded-xl border border-purple-200 font-semibold flex items-center gap-2">
                  <FaUserShield className="text-purple-600" />
                  Transport with Guardian
                </div>
              )}
            </section>

            {/* School Documents */}
            <section className="mt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <FaFilePdf className="mr-2 text-blue-600" /> Documents &
                Certificates
              </h3>
              <DetailItem
                icon={FaFilePdf}
                label="School Leaving Certificate (SLC)"
                value={student.slcFile}
                isLink
              />
            </section>
          </div>
        </div>

        {/* System Metadata */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-500 flex flex-wrap gap-6 items-center">
          <div className="flex items-center gap-2">
            <FaUser className="text-gray-400" />
            <span>
              Created By:{" "}
              <span className="font-semibold text-gray-700">
                {student.createdByUser?.fullName || "System Admin"}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FaClock className="text-gray-400" />
            <span>
              Created:{" "}
              <span className="font-semibold text-gray-700">
                {student.createdAt
                  ? new Date(student.createdAt).toLocaleDateString("en-IN")
                  : "-"}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FaClock className="text-gray-400" />
            <span>
              Last Updated:{" "}
              <span className="font-semibold text-gray-700">
                {student.updatedAt
                  ? new Date(student.updatedAt).toLocaleDateString("en-IN")
                  : "-"}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
